import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FiStar, FiCheck, FiArrowRight, FiLock, FiMessageSquare, FiHeart, FiAlertCircle } from 'react-icons/fi';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GuestFeedback = () => {
  const [searchParams] = useSearchParams();
  const prefillBooking = searchParams.get('booking') || '';

  const [step, setStep] = useState(1); // 1 = verify, 2 = form, 3 = thank you
  const [verifyBookingRef, setVerifyBookingRef] = useState(prefillBooking);
  const [verifyPhone, setVerifyPhone] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyErr, setVerifyErr] = useState('');

  const [booking, setBooking] = useState(null); // after verification
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [villaName, setVillaName] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [loved, setLoved] = useState('');
  const [improve, setImprove] = useState('');
  const [recommend, setRecommend] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');

  // Auto-verify when a ?booking= param is provided
  useEffect(() => {
    if (prefillBooking) setVerifyBookingRef(prefillBooking);
  }, [prefillBooking]);

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    setVerifyErr('');
    if (!verifyBookingRef && !verifyPhone) {
      setVerifyErr('Please enter your Booking ID or your Phone number.');
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/guest-feedback/verify-booking`, {
        booking_ref: verifyBookingRef,
        phone: verifyPhone,
      });
      const b = res.data;
      setBooking(b);
      setName(b.guest_name || '');
      setEmail(b.guest_email || '');
      setPhone(b.guest_phone || verifyPhone || '');
      setVillaName(b.villa_name || '');
      setCheckOut(b.check_out || '');
      setStep(2);
    } catch (err) {
      setVerifyErr(err?.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitErr('');
    if (rating < 1 || rating > 5) { setSubmitErr('Please pick a star rating.'); return; }
    if (recommend === null) { setSubmitErr('Please let us know if you would recommend us.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/guest-feedback/submit`, {
        booking_id: booking?.booking_id,
        phone,
        name,
        email,
        villa_id: booking?.villa_id,
        villa_name: villaName,
        check_out: checkOut,
        rating,
        loved,
        improve,
        recommend,
      });
      setStep(3);
    } catch (err) {
      setSubmitErr(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F7F8FA] to-white pt-28 pb-16">
      <SEO
        title="Guest Feedback — Royal Garden Villas, Lonavala"
        description="Share your post-stay experience at Royal Garden Villas, Lonavala. Rate your villa, tell us what you loved and what we can improve. Verified guests only."
        path="/feedback"
      />
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <p className="text-[#F57C20] text-[11px] font-bold tracking-[0.3em] uppercase mb-3">Guest Feedback</p>
          <h1 className="text-3xl md:text-4xl text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            How was your stay with us?
          </h1>
          <p className="text-[#6B7280] text-sm md:text-base max-w-xl mx-auto">
            Your honest feedback helps us get better and helps future guests choose the right villa. It takes under a minute.
          </p>
        </div>

        {/* STEP 1 — Verify booking */}
        {step === 1 && (
          <div data-testid="feedback-step-verify" className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-2 text-xs text-[#F57C20] font-semibold uppercase tracking-wider mb-3">
              <FiLock className="w-3.5 h-3.5" /> Step 1 of 2 · Verify your stay
            </div>
            <h2 className="text-xl text-[#1A1A2E] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              Confirm your booking
            </h2>
            <p className="text-[#6B7280] text-sm mb-5">
              Enter your Booking ID (from your confirmation email / WhatsApp) <strong>or</strong> the phone number used while booking.
            </p>
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">Booking ID (optional)</label>
                <input
                  type="text"
                  data-testid="feedback-verify-booking-id"
                  value={verifyBookingRef}
                  onChange={(e) => setVerifyBookingRef(e.target.value)}
                  placeholder="e.g. RGV-12345 or last 6 digits"
                  className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                />
              </div>
              <div className="relative text-center text-[11px] text-[#9CA3AF] uppercase tracking-widest">
                <span className="bg-white px-2 relative z-10">or</span>
                <div className="absolute left-0 right-0 top-1/2 border-t border-gray-100 -z-0" />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">Phone number used while booking</label>
                <input
                  type="tel"
                  data-testid="feedback-verify-phone"
                  value={verifyPhone}
                  onChange={(e) => setVerifyPhone(e.target.value)}
                  placeholder="+91 98XXX XXXXX"
                  className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                />
              </div>
              {verifyErr && (
                <p data-testid="feedback-verify-err" className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  <FiAlertCircle className="w-4 h-4" />{verifyErr}
                </p>
              )}
              <button
                type="submit"
                data-testid="feedback-verify-submit"
                disabled={verifying}
                className="w-full bg-[#1A1A2E] hover:bg-[#111] text-white px-6 py-3 rounded-lg text-sm font-semibold tracking-wide transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {verifying ? 'Verifying…' : (<>Continue <FiArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
            <p className="text-[11px] text-[#9CA3AF] mt-4 text-center">
              Trouble finding your Booking ID? WhatsApp us on <a href="https://wa.me/919137893277" className="text-[#F57C20] font-semibold">+91 91378 93277</a>
            </p>
          </div>
        )}

        {/* STEP 2 — Feedback form */}
        {step === 2 && (
          <div data-testid="feedback-step-form" className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-2 text-xs text-[#F57C20] font-semibold uppercase tracking-wider mb-3">
              <FiMessageSquare className="w-3.5 h-3.5" /> Step 2 of 2 · Share your feedback
            </div>
            {booking && (
              <div className="bg-[#FFF7EC] border border-[#F57C20]/30 rounded-lg px-4 py-3 mb-5 flex items-center gap-2 text-sm text-[#1A1A2E]">
                <FiCheck className="w-4 h-4 text-[#F57C20]" />
                Verified: <strong>{villaName}</strong> &middot; checkout {checkOut}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Prefilled read-only identity */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="feedback-name" required
                    className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">Phone</label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="feedback-phone" required
                    className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="feedback-email" required
                    className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">Check-out date</label>
                  <input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid="feedback-checkout"
                    className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" />
                </div>
              </div>

              {/* Rating stars */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-2">Overall rating</label>
                <div data-testid="feedback-rating" className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`feedback-star-${n}`}
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHoverRating(n)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <FiStar
                        className="w-8 h-8"
                        strokeWidth={1.5}
                        style={{
                          color: (hoverRating || rating) >= n ? '#F57C20' : '#D1D5DB',
                          fill: (hoverRating || rating) >= n ? '#F57C20' : 'transparent',
                        }}
                      />
                    </button>
                  ))}
                  <span className="text-[#6B7280] text-sm ml-3">{rating ? `${rating} of 5` : 'Pick a rating'}</span>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">What did you love most?</label>
                <textarea rows={3} value={loved} onChange={(e) => setLoved(e.target.value)} data-testid="feedback-loved"
                  placeholder="e.g. The private pool at sunset, the caretaker's hospitality, the quiet lawn in the mornings…"
                  className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" />
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-1.5">What can we improve?</label>
                <textarea rows={3} value={improve} onChange={(e) => setImprove(e.target.value)} data-testid="feedback-improve"
                  placeholder="Honest, specific feedback helps us the most. (Anything from kitchen stock to a dim bulb — we read every word.)"
                  className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" />
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mb-2">Would you recommend Royal Garden Villas to friends &amp; family?</label>
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="feedback-recommend-yes" onClick={() => setRecommend(true)}
                    className={`flex-1 px-4 py-3 rounded-lg border text-sm font-semibold transition-all ${recommend === true ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-gray-200 text-[#6B7280] hover:border-emerald-300'}`}>
                    Yes, happily
                  </button>
                  <button type="button" data-testid="feedback-recommend-no" onClick={() => setRecommend(false)}
                    className={`flex-1 px-4 py-3 rounded-lg border text-sm font-semibold transition-all ${recommend === false ? 'bg-red-50 border-red-500 text-red-700' : 'bg-white border-gray-200 text-[#6B7280] hover:border-red-300'}`}>
                    Not yet
                  </button>
                </div>
              </div>

              {submitErr && (
                <p data-testid="feedback-submit-err" className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  <FiAlertCircle className="w-4 h-4" />{submitErr}
                </p>
              )}

              <button
                type="submit"
                data-testid="feedback-submit"
                disabled={submitting}
                className="w-full bg-[#F57C20] hover:bg-[#E06A10] text-white px-6 py-3 rounded-lg text-sm font-semibold tracking-wide transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? 'Sending…' : (<>Submit Feedback <FiArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          </div>
        )}

        {/* STEP 3 — Thank you */}
        {step === 3 && (
          <div data-testid="feedback-step-thanks" className="bg-white border border-gray-200 rounded-2xl p-8 md:p-10 shadow-sm text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-5">
              <FiHeart className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Thank you, {name?.split(' ')[0] || 'friend'}!
            </h2>
            <p className="text-[#6B7280] text-sm md:text-base max-w-md mx-auto mb-6">
              Your feedback has reached the team at <strong>royalgardenlonavala@gmail.com</strong>. We read every word and act on it — thank you for taking the time.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" data-testid="feedback-cta-home"
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-[#1A1A2E] hover:bg-gray-50">
                Back to home
              </Link>
              <a href="https://wa.me/919137893277?text=Hi%20Royal%20Garden%20Villas%20team" target="_blank" rel="noreferrer"
                className="px-5 py-2.5 bg-[#25D366] rounded-lg text-sm font-semibold text-white hover:bg-[#1DA851]">
                WhatsApp us
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestFeedback;
